import { useEffect, useState } from 'react'
import { useAppSelector } from '../../../../state/hooks'
import {
  selectSaleId,
  selectSaleAttributesHasPaymentRequest,
} from '../../../../state/slices/currentSale/currentSaleSlice'
import {
  checkAndFetchPaymentRequests,
  PaymentRequestQueryResponse,
} from '../../../../functions/invoicing/fetch-invoicing-payment-requests'

interface UsePaymentRequestsResult {
  paymentRequests: PaymentRequestQueryResponse[]
  isLoading: boolean
  error: string | null
}

/**
 * Custom hook to fetch and manage payment requests for the current sale
 * Only fetches if:
 * - invoicing_payment_requests feature flag is enabled
 * - Sale has the 'paymentrequest' attribute
 * - Sale has a valid ID
 */
export const usePaymentRequests = (): UsePaymentRequestsResult => {
  const saleId = useAppSelector(selectSaleId)
  const hasPaymentRequestAttribute = useAppSelector(
    selectSaleAttributesHasPaymentRequest
  )
  const [paymentRequests, setPaymentRequests] = useState<
    PaymentRequestQueryResponse[]
  >([])
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    const fetchPaymentRequests = async () => {
      // Reset state
      setIsLoading(true)
      setError(null)

      try {
        const result = await checkAndFetchPaymentRequests(
          saleId,
          hasPaymentRequestAttribute
        )

        if (result === null) {
          // Feature not enabled or conditions not met
          setPaymentRequests([])
          setIsLoading(false)
          return
        }

        if (result.success && result.paymentRequests) {
          setPaymentRequests(result.paymentRequests.data)
        } else {
          setError(result.error || 'Failed to fetch payment requests')
          setPaymentRequests([])
        }
      } catch (err: any) {
        setError(err?.message || 'An unexpected error occurred')
        setPaymentRequests([])
      } finally {
        setIsLoading(false)
      }
    }

    // Only fetch if we have both saleId and the payment request attribute
    if (saleId && hasPaymentRequestAttribute) {
      fetchPaymentRequests()
    } else {
      // Clear data if conditions are not met
      setPaymentRequests([])
      setIsLoading(false)
      setError(null)
    }
  }, [saleId, hasPaymentRequestAttribute])

  return {
    paymentRequests,
    isLoading,
    error,
  }
}
